<template>
  <div class="h-full w-full relative text-white">
    <video
      v-video-reload="{
        show,
        unmuted,
        preloadNextSlotNow,
        seamlessIncomingFromLink,
        presentationHoldOverlay,
        src,
        useHlsJs,
        hlsConfig,
        setPlaybackIssue,
        capacityTier: state.capacityTier,
        lowMemoryMode: state.lowMemoryMode,
      }"
      :src="videoSrc"
      :poster="VIDEO_POSTER_SRC"
      crossorigin="anonymous"
      muted
      loop
      playsinline
      class="h-full w-full bg-black object-center peer/video"
      :class="[
        fit === 'cover' ? 'object-cover' : 'object-contain',
        show ? '' : preloadNextSlotNow ? 'player-video-offscreen-preload pointer-events-none' : 'hidden pointer-events-none',
      ]"
    />
    <SuppressedSectionNotice
      v-if="show && playbackIssue"
      class="absolute inset-0 z-20 min-h-0"
      title="Playback error"
      :reason="playbackIssue"
    />
    <span
      class="absolute top-0 right-0 text-shadow-lg shadow-black bg-black/50 p-0.5 rounded-bl-lg opacity-0 peer-data-[muted='true']/video:opacity-100 transition-opacity flex items-center gap-0.5"
    >
      <VolumeX class="size-4" />
      <X class="size-4" />
    </span>
  </div>
</template>

<script lang="ts" setup>
import type { TSectionFit } from '@lucimark/shared/helpers/sectionMediaFit'
import type { ErrorData } from 'hls.js'
import type { Directive } from 'vue'
import type { HlsCapacityConfig } from '@/composables/useHls'
import type { TVideoPlaybackSentryKind } from '@/helpers/reportPlayerVideoPlaybackToSentry'
import { VolumeX, X } from 'lucide-vue-next'
import { computed, inject, ref } from 'vue'
import SuppressedSectionNotice from '@/components/SuppressedSectionNotice.vue'
import { attachHls, destroyHls, getUseHlsJs, isHlsUrl } from '@/composables/useHls'
import usePlayer from '@/composables/usePlayer'
import { VIDEO_POSTER_SRC } from '@/constants'
import {
  SECTION_PLAYBACK_HLS_NOT_SUPPORTED_IN_WEBVIEW,
  SECTION_PLAYBACK_HLS_STREAM_ERROR,
  SECTION_PLAYBACK_VIDEO_DECODE_OR_FORMAT,
  SECTION_PLAYBACK_VIDEO_GENERIC,
  SECTION_PLAYBACK_VIDEO_NETWORK
} from '@/constants/sectionSuppressionMessages'
import {
  performPlaybackRecoveryReload,
  playbackFailureBucketFromSignals,
  recordPlaybackFailureForReloadPolicy
} from '@/helpers/playbackFailureReloadPolicy'
import {
  isBenignVideoPlayInterruptError,
  reportPlayerVideoPlaybackToSentry
} from '@/helpers/reportPlayerVideoPlaybackToSentry'
import { tryRemountVideoAfterDecodeError } from '@/helpers/tryRemountVideoAfterDecodeError'
import {
  clearVideoPlayEpochState,
  getVideoPlayEpoch,
  invalidateVideoPlayEpoch,
  isVideoPlayEpochCurrent,
  markVideoPlayEnsureSucceeded,
  scheduleEnsureVideoPlaying,
  setVideoPlayIntent,
  shouldAttemptVideoPlay,
  shouldEnsureVideoPlaying
} from '@/helpers/videoPlayEpoch'
import { notifyMediaLoopDeferredBoundary } from '@/helpers/mediaLoopDeferredBoundary'

const props = withDefaults(
  defineProps<{
    show?: boolean
    src: string
    unmuted?: boolean
    /** True when the hidden buffer slot should warm the next clip (keeps video in DOM). */
    preloadNextSlotNow?: boolean
    /** Skip extra paint delay before play() when following a playlist continuous link. */
    seamlessIncomingFromLink?: boolean
    /** Start offset (seconds) when continuing the same asset across consecutive frames (portal CPS splits). */
    continuousMediaOffsetSeconds?: number
    /**
     * Sticky outgoing overlay (Phase B): keep the element visible/painted but do not call play()
     * so PoP is not re-pinned to the outgoing clip.
     */
    presentationHoldOverlay?: boolean
    /** Set when asset is HLS (e.g. manifest URL). When unset, HLS is detected from src (e.g. .m3u8). */
    isHls?: boolean
    /** Omitted = historical video default (contain / letterbox). */
    fit?: TSectionFit
  }>(),
  {
    seamlessIncomingFromLink: false,
    continuousMediaOffsetSeconds: 0,
    preloadNextSlotNow: false,
    presentationHoldOverlay: false
  }
)

const { state } = usePlayer()

const reportHoldPaintReady = inject<((info: { type: 'video', src: string }) => void) | null>(
  'reportHoldPaintReady',
  null
)

const playbackIssue = ref<string | null>(null)

function setPlaybackIssue(msg: string | null) {
  playbackIssue.value = msg
}

const isHls = computed(() => props.isHls ?? isHlsUrl(props.src))

const useHlsJs = computed(() => getUseHlsJs(isHls.value, props.src))

/**
 * Capacity-tier-aware HLS.js buffer config.
 * Mid-tier devices default to 30 s max buffer / 600 s max-max, which is excessive for signage.
 * Low-memory mode and low capacity use the tightest caps (S2) so MSE does not fight the on-air
 * decoder on SlimBOX-class devices (video preload is also off on low — see shouldPreloadNextVideoSlot).
 * High / unknown tier gets explicit caps vs hls.js defaults (large max-max buffer).
 */
const hlsConfig = computed((): HlsCapacityConfig => {
  if (state.lowMemoryMode || state.capacityTier === 'low') {
    return { maxBufferLength: 6, maxMaxBufferLength: 20 }
  }
  if (state.capacityTier === 'mid') {
    return { maxBufferLength: 10, maxMaxBufferLength: 30 }
  }
  if (state.capacityTier === 'high') {
    return { maxBufferLength: 15, maxMaxBufferLength: 45 }
  }
  return { maxBufferLength: 12, maxMaxBufferLength: 40 }
})

/** When using hls.js we must not set video.src; Hls attaches via MSE. */
const videoSrc = computed(() => (useHlsJs.value ? '' : props.src))

// https://developer.chrome.com/blog/play-request-was-interrupted
// https://stackoverflow.com/questions/36803176/how-to-prevent-the-play-request-was-interrupted-by-a-call-to-pause-error
function isPlaying(el: HTMLVideoElement) {
  return el.currentTime > 0 && !el.paused && !el.ended && el.readyState > el.HAVE_CURRENT_DATA
}

/** Store on element so we can remove in updated/beforeUnmount. */
const ENDED_HANDLER_KEY = '__videoContainerEnded'
/** Last src we triggered load() for when hidden (preload once per slot). */
const PRELOADED_SRC_KEY = '__videoContainerPreloadedSrc'
/** Points directive / playVideo at the current `setPlaybackIssue` so callbacks stay fresh across updates. */
const PLAYBACK_ISSUE_SETTER_KEY = '__videoPlaybackIssueSetter'
/** Latest `src` / tier for Sentry (scrubbed in helper); synced each directive mount/update. */
const PLAYBACK_REPORT_CTX_KEY = '__videoPlaybackReportCtx'

interface PlaybackReportCtx {
  src: string
  useHlsJs: boolean
  isHls: boolean
  capacityTier: 'low' | 'mid' | 'high' | null
  lowMemoryMode: boolean
}

function setPlaybackReportCtx(el: HTMLVideoElement, ctx: PlaybackReportCtx) {
  (el as unknown as Record<string, PlaybackReportCtx>)[PLAYBACK_REPORT_CTX_KEY] = ctx
}

function getPlaybackReportCtx(el: HTMLVideoElement): PlaybackReportCtx | undefined {
  return (el as unknown as Record<string, PlaybackReportCtx | undefined>)[PLAYBACK_REPORT_CTX_KEY]
}

function sentryPlaybackBase(el: HTMLVideoElement) {
  const ctx = getPlaybackReportCtx(el)
  return {
    playbackSrc: ctx?.src ?? '',
    useHlsJs: ctx?.useHlsJs ?? false,
    isHls: ctx?.isHls ?? false,
    capacityTier: ctx?.capacityTier ?? null,
    lowMemoryMode: ctx?.lowMemoryMode ?? false
  }
}

function emitPlaybackFailure(
  el: HTMLVideoElement,
  payload: {
    kind: TVideoPlaybackSentryKind
    userVisibleReason?: string
    mediaErrorCode?: number
    mediaErrorMessage?: string
    videoReadyState?: number
    videoNetworkState?: number
    hlsErrorData?: ErrorData
    caughtError?: unknown
  }
) {
  const { lowMemoryMode, ...sentryRest } = sentryPlaybackBase(el)
  reportPlayerVideoPlaybackToSentry({
    ...payload,
    ...sentryRest,
    lowMemoryMode
  })
  const bucket = playbackFailureBucketFromSignals(payload.kind, payload.mediaErrorCode)
  const decision = recordPlaybackFailureForReloadPolicy({
    bucket,
    lowMemoryMode,
    caughtError: payload.caughtError
  })
  if (decision.reload && decision.reason != null) {
    performPlaybackRecoveryReload(decision.reason)
  }
}

function reportPlaybackIssueForEl(el: HTMLVideoElement, msg: string | null) {
  const set = (el as unknown as Record<string, ((m: string | null) => void) | undefined>)[PLAYBACK_ISSUE_SETTER_KEY]
  set?.(msg)
}

function syncPlaybackIssueSetter(el: HTMLVideoElement, setPlaybackIssue: ((msg: string | null) => void) | undefined) {
  (el as unknown as Record<string, (m: string | null) => void>)[PLAYBACK_ISSUE_SETTER_KEY]
    = setPlaybackIssue ?? ((_msg: string | null) => {})
}

function mediaErrorCodeToPlaybackMessage(code: number | undefined): string {
  if (code === MediaError.MEDIA_ERR_NETWORK) {
    return SECTION_PLAYBACK_VIDEO_NETWORK
  }
  if (code === MediaError.MEDIA_ERR_DECODE || code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
    return SECTION_PLAYBACK_VIDEO_DECODE_OR_FORMAT
  }
  return SECTION_PLAYBACK_VIDEO_GENERIC
}

function onVideoPlaybackError(ev: Event) {
  const el = ev.currentTarget as HTMLVideoElement
  const code = el.error?.code
  const ctx = getPlaybackReportCtx(el)
  // PLAYER-19: one soft remount for wedged progressive/blob decode before overlay / Sentry /
  // reload policy. Skip preload/hold slots, detached elements, and hls.js (MSE attach owns src).
  if (
    (code === MediaError.MEDIA_ERR_DECODE || code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED)
    && ctx != null
    && !ctx.useHlsJs
    && !ctx.isHls
    && shouldAttemptVideoPlay(el)
    && tryRemountVideoAfterDecodeError(el)
  ) {
    reportPlaybackIssueForEl(el, null)
    void playVideo(el, props.unmuted, msg => reportPlaybackIssueForEl(el, msg))
    return
  }
  const msg = mediaErrorCodeToPlaybackMessage(code)
  reportPlaybackIssueForEl(el, msg)
  emitPlaybackFailure(el, {
    kind: 'media_error',
    userVisibleReason: msg,
    mediaErrorCode: code,
    mediaErrorMessage: el.error?.message,
    videoReadyState: el.readyState,
    videoNetworkState: el.networkState
  })
}

function onVideoPlaybackPlaying(ev: Event) {
  const el = ev.currentTarget as HTMLVideoElement
  reportPlaybackIssueForEl(el, null)
  // On-air playing (not off-screen preload / sticky overlay) — clear sticky outgoing hold (Phase B).
  if (props.show === true && props.presentationHoldOverlay !== true) {
    reportHoldPaintReady?.({ type: 'video', src: props.src })
  }
}

function applyContinuousMediaOffsetIfNeeded(
  el: HTMLVideoElement,
  seamless: boolean,
  offsetSeconds: number
): void {
  if (!seamless || offsetSeconds <= 0 || !Number.isFinite(offsetSeconds)) {
    return
  }
  const apply = () => {
    try {
      if (el.readyState >= HTMLMediaElement.HAVE_METADATA) {
        el.currentTime = offsetSeconds
      }
    }
    catch {
      /* ignore seek before metadata */
    }
  }
  if (el.readyState >= HTMLMediaElement.HAVE_METADATA) {
    apply()
  }
  else {
    el.addEventListener('loadedmetadata', apply, { once: true })
  }
}

function attachHlsReporting(el: HTMLVideoElement, src: string, useHlsJs: boolean, hlsConfig?: HlsCapacityConfig) {
  attachHls(el, src, useHlsJs, hlsConfig, {
    onUnsupportedInBrowser: () => {
      reportPlaybackIssueForEl(el, SECTION_PLAYBACK_HLS_NOT_SUPPORTED_IN_WEBVIEW)
      emitPlaybackFailure(el, {
        kind: 'hls_unsupported',
        userVisibleReason: SECTION_PLAYBACK_HLS_NOT_SUPPORTED_IN_WEBVIEW
      })
    },
    onFatalStreamError: (data) => {
      reportPlaybackIssueForEl(el, SECTION_PLAYBACK_HLS_STREAM_ERROR)
      emitPlaybackFailure(el, {
        kind: 'hls_fatal',
        userVisibleReason: SECTION_PLAYBACK_HLS_STREAM_ERROR,
        hlsErrorData: data
      })
    }
  })
}

/**
 * Android WebView: `loop` can be unreliable; `ended` may fire and freeze. Restart from 0 so the frame
 * keeps moving until the scheduler advances.
 * **Web (Chrome):** do not install this — native `loop` is enough; the `ended` + `currentTime = 0` path
 * causes a visible flash at EOF when the file is shorter than the frame duration (user report).
 */
function installLoopFallback(el: HTMLVideoElement) {
  if ((el as unknown as Record<string, unknown>)[ENDED_HANDLER_KEY]) {
    return
  }
  const handler = () => {
    if (el.paused || el.ended) {
      el.currentTime = 0
      void el.play().catch(() => {
        /* Benign when the scheduler tears down or pauses during loop restart */
      })
      // Same frame id — still a loop boundary for deferred registration schedule flush (#1577).
      notifyMediaLoopDeferredBoundary()
    }
  }
  ;(el as unknown as Record<string, unknown>)[ENDED_HANDLER_KEY] = handler
  el.addEventListener('ended', handler)
}

function removeLoopFallback(el: HTMLVideoElement) {
  const handler = (el as unknown as Record<string, unknown>)[ENDED_HANDLER_KEY] as (() => void) | undefined
  if (handler) {
    el.removeEventListener('ended', handler)
    delete (el as unknown as Record<string, unknown>)[ENDED_HANDLER_KEY]
  }
}

/**
 * Android WebView often ignores `muted` alone (OEM / HLS). Volume 0 is what actually silences the element.
 * Native Media3 slots do not use this element — see hybrid `muted`.
 */
function applyVideoElementAudibility(el: HTMLVideoElement, unmuted: boolean | undefined) {
  const audible = unmuted === true
  el.muted = !audible
  el.volume = audible ? 1 : 0
}

async function playVideo(
  el: HTMLVideoElement,
  unmuted?: boolean,
  setPlaybackIssue?: (msg: string | null) => void
) {
  if (!shouldAttemptVideoPlay(el)) {
    return
  }
  // Capture only — do not bump here. Concurrent on-air play attempts share the epoch;
  // pause/hold/hide/unmount invalidate via {@link pauseVideoInvalidatingPlay}.
  const epoch = getVideoPlayEpoch(el)
  if (el.muted) {
    el.addEventListener('click', () => {
      void playVideo(el, unmuted, setPlaybackIssue)
    }, { once: true })
  }

  const exitMaybeEnsure = () => {
    if (shouldEnsureVideoPlaying(el, isPlaying(el))) {
      scheduleEnsureVideoPlaying(el, () => {
        void playVideo(el, unmuted, setPlaybackIssue)
      })
    }
  }

  try {
    try {
      if (!isVideoPlayEpochCurrent(el, epoch) || !shouldAttemptVideoPlay(el)) {
        exitMaybeEnsure()
        return
      }
      applyVideoElementAudibility(el, unmuted)
      if (!isPlaying(el)) {
        await el.play()
      }
    }
    catch (error) {
      if (!isVideoPlayEpochCurrent(el, epoch) || !shouldAttemptVideoPlay(el)) {
        exitMaybeEnsure()
        return
      }
      // Benign AbortError when pause/unmount races play() — expected on clip transitions.
      if (!isBenignVideoPlayInterruptError(error)) {
        console.warn(error)
      }
      applyVideoElementAudibility(el, false)
      if (!isPlaying(el)) {
        await el.play()
      }
    }
    finally {
      if (unmuted && isVideoPlayEpochCurrent(el, epoch)) {
        el.setAttribute('data-muted', `${el.muted}`)
      }
    }
    if (!isVideoPlayEpochCurrent(el, epoch) || !shouldAttemptVideoPlay(el)) {
      exitMaybeEnsure()
      return
    }
    installLoopFallback(el)
    markVideoPlayEnsureSucceeded(el)
  }
  catch (err) {
    if (!isVideoPlayEpochCurrent(el, epoch) || !shouldAttemptVideoPlay(el)) {
      exitMaybeEnsure()
      return
    }
    // Transition interrupt: do not surface overlay or count toward recovery reload.
    if (isBenignVideoPlayInterruptError(err)) {
      exitMaybeEnsure()
      return
    }
    console.warn('VideoContainer: play failed (will not reset player)', err)
    setPlaybackIssue?.(SECTION_PLAYBACK_VIDEO_GENERIC)
    emitPlaybackFailure(el, {
      kind: 'play_failed',
      userVisibleReason: SECTION_PLAYBACK_VIDEO_GENERIC,
      caughtError: err
    })
  }
}

/** Pause and invalidate any in-flight play() so AbortError settles as stale. */
function pauseVideoInvalidatingPlay(el: HTMLVideoElement) {
  invalidateVideoPlayEpoch(el)
  if (isPlaying(el) || !el.paused) {
    el.pause()
  }
}

/**
 * When the hidden slot needs a new `src`, we only bump `preload` — we must NOT call `el.load()` while
 * `show` is false: runtime logs showed every `load()` with the slot hidden within ms of a buffer commit,
 * and Chrome can still composite the first decoded frame on top (flash) despite visibility:hidden.
 * Call {@link loadVideoElementIfSrcChanged} when the slot becomes visible before `play()`.
 */
function triggerPreloadIfNeeded(el: HTMLVideoElement, opts: { allowElementLoad: boolean }) {
  const lastPreloaded = (el as unknown as Record<string, string>)[PRELOADED_SRC_KEY]
  if (!el.src || lastPreloaded === el.src) {
    return
  }
  if (!opts.allowElementLoad) {
    return
  }
  ;(el as unknown as Record<string, string>)[PRELOADED_SRC_KEY] = el.src
  el.load()
}

function loadVideoElementIfSrcChanged(el: HTMLVideoElement) {
  const lastPreloaded = (el as unknown as Record<string, string>)[PRELOADED_SRC_KEY]
  if (el.src && lastPreloaded !== el.src) {
    ;(el as unknown as Record<string, string>)[PRELOADED_SRC_KEY] = el.src
    el.load()
  }
}

/** Let Chrome apply z-index / visibility on the buffer before starting decode-visible playback (reduces first-frame flash). */
function afterNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve())
    })
  })
}

const DEFER_REVEAL_STATE_KEY = '__videoDeferRevealState'

interface DeferRevealState {
  cleanup: () => void
}

function cancelDeferredVideoReveal(el: HTMLVideoElement): void {
  const bag = el as unknown as Record<string, DeferRevealState | undefined>
  bag[DEFER_REVEAL_STATE_KEY]?.cleanup()
  delete bag[DEFER_REVEAL_STATE_KEY]
}

/**
 * When a two-slot buffer slot becomes visible, the `<video>` can paint black or a stale first frame for a
 * frame before decode/play settles. Keep `visibility: hidden` on the element until `playing` (or error /
 * timeout) so the swap does not flicker ahead of the real sequence.
 */
function startDeferredVideoReveal(el: HTMLVideoElement, seamless: boolean): void {
  cancelDeferredVideoReveal(el)
  if (seamless) {
    el.style.visibility = ''
    return
  }
  el.style.visibility = 'hidden'
  let done = false
  /** DOM `setTimeout` handle (numeric in browsers; avoid `NodeJS.Timeout` from ambient Node typings). */
  let tid: number | undefined
  const reveal = () => {
    if (done) {
      return
    }
    done = true
    el.removeEventListener('playing', reveal)
    el.removeEventListener('error', reveal)
    if (tid !== undefined) {
      window.clearTimeout(tid)
      tid = undefined
    }
    const bag = el as unknown as Record<string, DeferRevealState | undefined>
    delete bag[DEFER_REVEAL_STATE_KEY]
    el.style.visibility = ''
  }
  const cancel = () => {
    if (done) {
      return
    }
    done = true
    el.removeEventListener('playing', reveal)
    el.removeEventListener('error', reveal)
    if (tid !== undefined) {
      window.clearTimeout(tid)
      tid = undefined
    }
    const bag = el as unknown as Record<string, DeferRevealState | undefined>
    delete bag[DEFER_REVEAL_STATE_KEY]
  }
  el.addEventListener('playing', reveal, { once: true })
  el.addEventListener('error', reveal, { once: true })
  tid = window.setTimeout(reveal, 4000)
  ;(el as unknown as Record<string, DeferRevealState>)[DEFER_REVEAL_STATE_KEY] = { cleanup: cancel }
}

interface VideoReloadBinding {
  show?: boolean
  unmuted?: boolean
  preloadNextSlotNow?: boolean
  seamlessIncomingFromLink?: boolean
  presentationHoldOverlay?: boolean
  continuousMediaOffsetSeconds?: number
  src: string
  useHlsJs: boolean
  hlsConfig?: HlsCapacityConfig
  setPlaybackIssue?: (msg: string | null) => void
  capacityTier?: 'low' | 'mid' | 'high' | null
  lowMemoryMode?: boolean
}

const vVideoReload: Directive<HTMLVideoElement, VideoReloadBinding> = {
  mounted: async (el, binding) => {
    const {
      show,
      unmuted,
      src,
      useHlsJs,
      preloadNextSlotNow,
      seamlessIncomingFromLink,
      presentationHoldOverlay,
      continuousMediaOffsetSeconds,
      hlsConfig,
      setPlaybackIssue,
      capacityTier,
      lowMemoryMode
    } = binding.value
    syncPlaybackIssueSetter(el, setPlaybackIssue)
    setPlaybackReportCtx(el, {
      src,
      useHlsJs,
      isHls: isHlsUrl(src),
      capacityTier: capacityTier ?? null,
      lowMemoryMode: lowMemoryMode === true
    })
    setVideoPlayIntent(el, {
      show: show === true,
      presentationHoldOverlay: presentationHoldOverlay === true
    })
    el.addEventListener('error', onVideoPlaybackError)
    el.addEventListener('playing', onVideoPlaybackPlaying)
    // Defer full preload until the slot is active or within the preload window; metadata only otherwise.
    el.preload = show || preloadNextSlotNow ? 'auto' : 'metadata'
    attachHlsReporting(el, src, useHlsJs, hlsConfig)
    if (presentationHoldOverlay === true && show) {
      // Paint-only overlay: visible, paused — must not drive PoP via syncPlayingFrameFromActiveVideo.
      cancelDeferredVideoReveal(el)
      el.style.visibility = ''
      if (!useHlsJs) {
        loadVideoElementIfSrcChanged(el)
      }
      pauseVideoInvalidatingPlay(el)
      return
    }
    if (!isPlaying(el) && show) {
      startDeferredVideoReveal(el, seamlessIncomingFromLink === true)
      if (!useHlsJs) {
        loadVideoElementIfSrcChanged(el)
      }
      applyContinuousMediaOffsetIfNeeded(
        el,
        seamlessIncomingFromLink === true,
        continuousMediaOffsetSeconds ?? 0
      )
      if (!seamlessIncomingFromLink) {
        await afterNextPaint()
      }
      if (!shouldAttemptVideoPlay(el)) {
        return
      }
      await playVideo(el, unmuted, setPlaybackIssue)
      if (shouldAttemptVideoPlay(el) && isPlaying(el)) {
        reportHoldPaintReady?.({ type: 'video', src })
      }
    }
  },
  updated: async (el, binding) => {
    const {
      show,
      unmuted,
      src,
      useHlsJs,
      preloadNextSlotNow,
      seamlessIncomingFromLink,
      presentationHoldOverlay,
      continuousMediaOffsetSeconds,
      hlsConfig,
      setPlaybackIssue,
      capacityTier,
      lowMemoryMode
    } = binding.value
    const prev = binding.oldValue
    syncPlaybackIssueSetter(el, setPlaybackIssue)
    setPlaybackReportCtx(el, {
      src,
      useHlsJs,
      isHls: isHlsUrl(src),
      capacityTier: capacityTier ?? null,
      lowMemoryMode: lowMemoryMode === true
    })
    setVideoPlayIntent(el, {
      show: show === true,
      presentationHoldOverlay: presentationHoldOverlay === true
    })
    const hlsNeedsReattach = prev && useHlsJs && (
      prev.src !== src
      || prev.useHlsJs !== useHlsJs
      || prev.lowMemoryMode !== lowMemoryMode
      || prev.capacityTier !== capacityTier
    )
    if (hlsNeedsReattach) {
      reportPlaybackIssueForEl(el, null)
      destroyHls(el)
      attachHlsReporting(el, src, useHlsJs, hlsConfig)
    }
    if (!show) {
      cancelDeferredVideoReveal(el)
      reportPlaybackIssueForEl(el, null)
      removeLoopFallback(el)
      // Do not reset currentTime when hiding: the two-slot buffer hides the off-screen slot while the
      // other frame is visible; zeroing here clears progress on clips that will be shown again (e.g. last
      // frame video), which looks like a mid-playback reload. Seek to start when the slot becomes visible.
      // Do not use visibility:hidden on the full-size element while preloading: Chrome may still composite
      // decoded video above the other buffer slot (e.g. green creative over tail image). Off-screen class instead.
      el.style.visibility = ''
      el.style.position = ''
      el.style.left = ''
      el.style.top = ''
      el.style.width = ''
      el.style.height = ''
      el.style.opacity = ''
      if (!preloadNextSlotNow) {
        el.style.visibility = 'hidden'
      }
      pauseVideoInvalidatingPlay(el)
      if (!useHlsJs && !preloadNextSlotNow) {
        el.removeAttribute('src')
        el.load()
      }
      if (!useHlsJs) {
        if (preloadNextSlotNow) {
          el.preload = 'auto'
          // Clear the load guard so we run a fresh {@link loadVideoElementIfSrcChanged} when this slot becomes visible.
          if (!prev?.preloadNextSlotNow) {
            ;(el as unknown as Record<string, string>)[PRELOADED_SRC_KEY] = ''
          }
          triggerPreloadIfNeeded(el, { allowElementLoad: false })
        }
        else {
          el.preload = 'metadata'
          triggerPreloadIfNeeded(el, { allowElementLoad: false })
        }
      }
    }
    if (show) {
      el.preload = 'auto'
      if (presentationHoldOverlay === true) {
        cancelDeferredVideoReveal(el)
        el.style.visibility = ''
        if (!useHlsJs) {
          loadVideoElementIfSrcChanged(el)
        }
        pauseVideoInvalidatingPlay(el)
        return
      }
      const becameVisible = prev == null || prev.show !== true
      const srcChanged = prev != null && prev.src !== src
      if (becameVisible) {
        if (isPlaying(el)) {
          cancelDeferredVideoReveal(el)
          el.style.visibility = ''
          // Preloaded/already-decoding slot: `playing` may not re-fire — ack sticky hold for Phase C fade.
          reportHoldPaintReady?.({ type: 'video', src })
        }
        else {
          startDeferredVideoReveal(el, seamlessIncomingFromLink === true)
        }
      }
      else {
        cancelDeferredVideoReveal(el)
        el.style.visibility = ''
      }
      if (!useHlsJs) {
        loadVideoElementIfSrcChanged(el)
      }
      const seamless = seamlessIncomingFromLink === true
      const offsetSec = continuousMediaOffsetSeconds ?? 0
      if (seamless) {
        applyContinuousMediaOffsetIfNeeded(el, true, offsetSec)
      }
      // Avoid redundant seek-to-0 when `src` just changed: :src already navigates the element, and an
      // extra currentTime=0 can flash the first decoded frame before z-index/viewIdx compositing settles.
      // When `src` is unchanged but we hid without resetting (buffer rotation), restart only if not at start.
      if (!srcChanged && becameVisible && !seamless && (el.currentTime > 0.05 || el.ended)) {
        el.currentTime = 0
      }
      if (becameVisible && !seamlessIncomingFromLink) {
        await afterNextPaint()
      }
      if (!shouldAttemptVideoPlay(el)) {
        return
      }
      await playVideo(el, unmuted, setPlaybackIssue)
      // `play()` can resolve after decode without a fresh `playing` event on some WebViews.
      if (shouldAttemptVideoPlay(el) && isPlaying(el)) {
        reportHoldPaintReady?.({ type: 'video', src })
      }
    }
  },
  beforeUnmount: (el, binding) => {
    const { unmuted } = binding.value
    cancelDeferredVideoReveal(el)
    el.removeEventListener('error', onVideoPlaybackError)
    el.removeEventListener('playing', onVideoPlaybackPlaying)
    delete (el as unknown as Record<string, unknown>)[PLAYBACK_ISSUE_SETTER_KEY]
    delete (el as unknown as Record<string, unknown>)[PLAYBACK_REPORT_CTX_KEY]
    removeLoopFallback(el)
    destroyHls(el)
    pauseVideoInvalidatingPlay(el)
    clearVideoPlayEpochState(el)
    el.removeAttribute('src')
    el.load()
    el.removeEventListener('click', () => playVideo(el, unmuted))
  }
}
</script>

<style scoped>
/* Hidden-slot preload: keep element in DOM for decode but off-screen (invisible still composites in Chrome). */
video.player-video-offscreen-preload {
  position: fixed !important;
  left: -100vw !important;
  top: 0 !important;
  width: 1px !important;
  height: 1px !important;
  max-width: 1px !important;
  max-height: 1px !important;
  opacity: 0 !important;
  visibility: hidden !important;
}
</style>
